import React, { type ReactNode } from 'react'

import {
  ActionIcon,
  Box,
  Group,
  ScrollArea,
  Table,
  type TableProps,
  Text,
} from '@mantine/core'
import {
  IconArrowDown,
  IconArrowUp,
  IconChevronDown,
} from '@tabler/icons-react'

import classes from './TowerDataTable.module.css'

export type TowerDataTableAccessor<T, R> = (item: T) => R

type ColumnExtras = {
  textAlign?: 'left' | 'center' | 'right'
  width?: number | string
  title: ReactNode
  sortable?: boolean
  groupId?: string
  groupHeader?: ReactNode
}

export type TowerDataTableColumn<T> = ColumnExtras & {
  accessor?: keyof T | TowerDataTableAccessor<T, unknown>
  render?: (value: unknown, item: T, index: number) => ReactNode
}

// helper function for defining a column that directly renders the value with safe typing
export function column<T, K extends keyof T>(
  config: ColumnExtras & {
    accessor: K
    render?: (value: T[K], item: T, index: number) => ReactNode
  },
): TowerDataTableColumn<T> {
  return config as TowerDataTableColumn<T>
}

// helper function for defining a column that remaps the underlying value with safe typing
export function mapColumn<T, R>(
  config: ColumnExtras & {
    accessor?: TowerDataTableAccessor<T, R>
    render?: (value: R, item: T, index: number) => ReactNode
  },
): TowerDataTableColumn<T> {
  return config as TowerDataTableColumn<T>
}

export interface TowerDataTableProps<T extends object>
  extends Omit<TableProps, 'data'> {
  data: T[]
  columns: TowerDataTableColumn<T>[]
  emptyMessage?: string
  maxHeight?: number | string
  onRowClick?: (item: T) => void
  isLoading?: boolean
  classNames?: Record<string, string | ((item: T) => string)>
  expandedRows?: Record<string, boolean>
  onRowExpand?: (item: T, index: number, isExpanded: boolean) => void
  renderExpandedContent?: (item: T) => ReactNode
  sortBy?: string | null
  sortDirection?: 'asc' | 'desc'
  onSort?: (
    columnIndex: number,
    accessor: keyof T | TowerDataTableAccessor<T, unknown> | undefined,
  ) => void
}

/**
 * a general-purpose data table component for use throughout tower which consolidates a bunch of styling
 * and basic behavior
 */
export function TowerDataTable<T extends object>({
  data,
  columns,
  emptyMessage = 'No data available',
  maxHeight,
  onRowClick,
  isLoading = false,
  expandedRows = {},
  onRowExpand,
  renderExpandedContent,
  sortBy,
  sortDirection,
  onSort,
  ...tableProps
}: TowerDataTableProps<T>) {
  const getRowKey = (item: T, index: number): string => {
    if ('id' in item) {
      return String((item as Record<string, unknown>)['id'])
    }
    return `row-${index}`
  }

  const getCellValue = (
    item: T,
    accessor: keyof T | TowerDataTableAccessor<T, unknown> | undefined,
  ): unknown => {
    if (!accessor) {
      return null
    }
    if (typeof accessor === 'function') {
      return accessor(item)
    }
    return item[accessor]
  }

  const columnGroups: Array<{
    groupId?: string
    groupHeader?: ReactNode
    columns: Array<{ column: TowerDataTableColumn<T>; index: number }>
  }> = []
  let currentGroup: {
    groupId?: string
    groupHeader?: ReactNode
    columns: Array<{ column: TowerDataTableColumn<T>; index: number }>
  } | null = null

  columns.forEach((col, index) => {
    if (col.groupId) {
      if (!currentGroup || currentGroup.groupId !== col.groupId) {
        currentGroup = {
          groupId: col.groupId,
          groupHeader: col.groupHeader,
          columns: [],
        }
        columnGroups.push(currentGroup)
      }
      if (!currentGroup.groupHeader && col.groupHeader) {
        currentGroup.groupHeader = col.groupHeader
      }
      currentGroup.columns.push({ column: col, index })
    } else {
      if (currentGroup && currentGroup.groupId) {
        currentGroup = null
      }
      if (!currentGroup) {
        currentGroup = { columns: [] }
        columnGroups.push(currentGroup)
      }
      currentGroup.columns.push({ column: col, index })
    }
  })

  const hasGroups = columnGroups.some((group) => group.groupId)

  const tableContent = (
    <Table className={classes.datatable} {...tableProps}>
      <Table.Thead>
        {hasGroups && (
          <Table.Tr>
            {columnGroups.map((group, groupIndex) => {
              if (group.groupId) {
                return (
                  <Table.Th
                    key={`group-${groupIndex}`}
                    colSpan={group.columns.length}
                    className={classes.group_header}
                  >
                    <Text fw={600} size="sm">
                      {group.groupHeader || group.groupId}
                    </Text>
                  </Table.Th>
                )
              }

              return group.columns.map((colData) => (
                <Table.Th
                  key={`ungrouped-${colData.index}`}
                  rowSpan={2}
                  className={`${classes.ungrouped_header} ${
                    colData.column.textAlign === 'center'
                      ? classes.cell_center
                      : colData.column.textAlign === 'right'
                        ? classes.cell_right
                        : classes.cell_left
                  }`}
                  style={{ width: colData.column.width }}
                >
                  <Text fw={600} size="sm">
                    {colData.column.title}
                  </Text>
                </Table.Th>
              ))
            })}
          </Table.Tr>
        )}
        <Table.Tr>
          {columns.map((col, index) => {
            const isSorted = sortBy === String(col.accessor)
            const canSort = col.sortable && onSort

            if (hasGroups && !col.groupId) {
              return null
            }

            return (
              <Table.Th
                key={`header-${index}`}
                className={`${
                  col.textAlign === 'center'
                    ? classes.cell_center
                    : col.textAlign === 'right'
                      ? classes.cell_right
                      : classes.cell_left
                } ${canSort ? classes.sortable_header : ''}`}
                style={{ width: col.width }}
                onClick={
                  canSort
                    ? () => {
                        onSort(index, col.accessor)
                      }
                    : undefined
                }
              >
                {canSort ? (
                  <Group
                    gap="xs"
                    wrap="nowrap"
                    justify={
                      col.textAlign === 'center'
                        ? 'center'
                        : col.textAlign === 'right'
                          ? 'flex-end'
                          : 'flex-start'
                    }
                  >
                    <Text>{col.title}</Text>
                    {isSorted &&
                      (sortDirection === 'asc' ? (
                        <IconArrowUp size={12} />
                      ) : (
                        <IconArrowDown size={12} />
                      ))}
                  </Group>
                ) : (
                  col.title
                )}
              </Table.Th>
            )
          })}
        </Table.Tr>
      </Table.Thead>

      <Table.Tbody>
        {data.length === 0 ? (
          <Table.Tr>
            <Table.Td
              colSpan={columns.length}
              className={classes.empty_message}
            >
              <Text size="sm" c="dimmed" py="lg">
                {isLoading ? 'Loading...' : emptyMessage}
              </Text>
            </Table.Td>
          </Table.Tr>
        ) : (
          data.map((item, rowIndex) => {
            const rowId = getRowKey(item, rowIndex)
            const isExpanded = expandedRows[rowId]
            return (
              <React.Fragment key={rowId}>
                <Table.Tr
                  className={`${isExpanded ? classes.expanded_row : ''} ${
                    tableProps.classNames?.row
                      ? typeof tableProps.classNames.row === 'function'
                        ? tableProps.classNames.row(item)
                        : tableProps.classNames.row
                      : ''
                  }`}
                  onClick={
                    onRowClick
                      ? () => {
                          onRowClick(item)
                        }
                      : undefined
                  }
                >
                  {columns.map((col, colIndex) => {
                    const value = getCellValue(item, col.accessor)
                    return (
                      <Table.Td
                        key={`cell-${rowIndex}-${colIndex}`}
                        className={
                          col.textAlign === 'center'
                            ? classes.cell_center
                            : col.textAlign === 'right'
                              ? classes.cell_right
                              : classes.cell_left
                        }
                      >
                        {colIndex === 0 &&
                        onRowExpand &&
                        renderExpandedContent ? (
                          <div className={classes.expand_cell_container}>
                            <ActionIcon
                              onClick={(e) => {
                                e.stopPropagation()
                                onRowExpand(item, rowIndex, !isExpanded)
                              }}
                              className={`${classes.expand_button} ${isExpanded ? classes.expand_button_expanded : ''}`}
                              size="sm"
                              variant="subtle"
                            >
                              <IconChevronDown size={16} />
                            </ActionIcon>
                            {col.render
                              ? col.render(value, item, rowIndex)
                              : (value as React.ReactNode)}
                          </div>
                        ) : col.render ? (
                          col.render(value, item, rowIndex)
                        ) : (
                          (value as React.ReactNode)
                        )}
                      </Table.Td>
                    )
                  })}
                </Table.Tr>

                {isExpanded && renderExpandedContent && (
                  <Table.Tr>
                    <Table.Td
                      colSpan={columns.length}
                      className={classes.expanded_content}
                    >
                      {renderExpandedContent(item)}
                    </Table.Td>
                  </Table.Tr>
                )}
              </React.Fragment>
            )
          })
        )}
      </Table.Tbody>
    </Table>
  )

  // bolt in scrolling if max-height is used or horizontal scrolling is needed
  return (
    <Box>
      <ScrollArea
        h={maxHeight}
        offsetScrollbars
        scrollbarSize={8}
        type="always"
      >
        {tableContent}
      </ScrollArea>
    </Box>
  )
}
